import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RestEndpoints } from "../../../../../api/endpoints";
import getInstance from "../../../../../utils/rest";
import { ICfmOrderX } from "../../../../domain/order-x/cfm-order-x";
import { CfmOrderXConverter } from "../../../models/converter/order-x/cfm-order-x.converter";
import { ICfmOrderXModel } from "../../../models/order-x/cfm-order-x.model";
import { CfmQueryKeys } from "../../cfm-query-keys";
import { ICfmOrderActionModel } from "../../../models/order-x/action/cfm-order-x-action.model";

interface IUpdateOrderXRequestData {
  action: ICfmOrderActionModel;
  orderId: number;
}

const updateOrderXQuery = async ({ action, orderId }: IUpdateOrderXRequestData): Promise<ICfmOrderX> => {
  const { data } = await getInstance().patch<ICfmOrderXModel>(
    RestEndpoints.CfmOrderX.replace(":id", orderId.toString()),
    action,
  );
  return CfmOrderXConverter.toDomain(data);
};

export const useUpdateOrderXQuery = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: updateOrderXQuery,

    onError: () => {
      enqueueSnackbar(t("notifications.order_saved_error.message"), {
        variant: "error",
      });
    },

    onSuccess: ({ id }) => {
      enqueueSnackbar(t("notifications.order_saved_success.message"), {
        variant: "success",
      });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrdersXWithFilterInfinite] });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrdersXWithFilter] });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrderXStatusCount] });
      void queryClient.invalidateQueries({
        queryKey: [CfmQueryKeys.GetOrderXActions, id]
      });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetOrderXHistory] });
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetCfmOrderResponseReport] });
    }
  });
};
